import { AuthToken } from '../value-objects/AuthToken';
import { AuthLocalStorageAdapter } from '../repositories/AuthLocalStorageAdapter';
import { IAuthRepository } from '../repositories/IAuthRepository';

/**
 * 토큰 상태
 */
export enum TokenStatus {
  VALID = 'valid',
  EXPIRED = 'expired',
  REFRESH_NEEDED = 'refresh_needed',
  INVALID = 'invalid',
  NOT_FOUND = 'not_found',
}

/**
 * 토큰 검증 결과
 */
export interface TokenValidationResult {
  status: TokenStatus;
  token?: AuthToken;
  expiresIn?: number; // 남은 시간(초)
  needsRefresh?: boolean;
  error?: string;
}

/**
 * 토큰 갱신 결과
 */
export interface TokenRefreshResult {
  success: boolean;
  token?: AuthToken;
  error?: string;
}

/**
 * JWT 페이로드 타입
 */
export interface JWTPayload {
  sub: string; // 사용자 ID
  email: string;
  role: string;
  iat: number; // 발급 시간
  exp: number; // 만료 시간
  aud?: string; // 대상
  iss?: string; // 발급자
  jti?: string; // JWT ID
}

/**
 * 토큰 관리 서비스
 * JWT 토큰의 생성, 검증, 갱신, 무효화를 담당
 */
export class TokenService {
  private static refreshPromise: Promise<TokenRefreshResult> | null = null;
  private refreshPromise: Promise<TokenRefreshResult> | null = null;

  constructor(private readonly authRepository: IAuthRepository) {}

  /**
   * 현재 토큰 가져오기
   */
  getCurrentToken(): AuthToken | null {
    return AuthLocalStorageAdapter.loadToken();
  }

  /**
   * 토큰 저장
   */
  saveToken(token: AuthToken): void {
    AuthLocalStorageAdapter.saveToken(token);
    AuthLocalStorageAdapter.updateLastActivity();
  }

  /**
   * 토큰 삭제
   */
  removeToken(): void {
    AuthLocalStorageAdapter.removeToken();
  }

  /**
   * 토큰 유효성 검증
   */
  validateToken(token?: AuthToken): TokenValidationResult {
    const targetToken = token || this.getCurrentToken();

    if (!targetToken) {
      return {
        status: TokenStatus.NOT_FOUND,
        error: '토큰이 존재하지 않습니다.',
      };
    }

    try {
      // 토큰 만료 확인
      if (targetToken.isExpired()) {
        return {
          status: TokenStatus.EXPIRED,
          token: targetToken,
          error: '토큰이 만료되었습니다.',
          needsRefresh: true,
        };
      }

      // 갱신 필요 확인 (만료 5분 전)
      if (targetToken.needsRefresh()) {
        const expiresIn = Math.floor((targetToken.expiresAt.getTime() - Date.now()) / 1000);
        return {
          status: TokenStatus.REFRESH_NEEDED,
          token: targetToken,
          expiresIn,
          needsRefresh: true,
        };
      }

      // 토큰 유효
      const expiresIn = Math.floor((targetToken.expiresAt.getTime() - Date.now()) / 1000);
      return {
        status: TokenStatus.VALID,
        token: targetToken,
        expiresIn,
        needsRefresh: false,
      };
    } catch (error) {
      return {
        status: TokenStatus.INVALID,
        error: error instanceof Error ? error.message : '토큰 검증 중 오류가 발생했습니다.',
      };
    }
  }

  /**
   * 토큰 갱신 (중복 요청 방지)
   */
  async refreshToken(forceRefresh = false): Promise<TokenRefreshResult> {
    // 이미 갱신 중인 요청이 있다면 기다림
    if (this.refreshPromise && !forceRefresh) {
      return await this.refreshPromise;
    }

    this.refreshPromise = this.performTokenRefresh();
    
    try {
      const result = await this.refreshPromise;
      return result;
    } finally {
      this.refreshPromise = null;
    }
  }

  /**
   * 실제 토큰 갱신 수행
   */
  private async performTokenRefresh(): Promise<TokenRefreshResult> {
    try {
      const currentToken = this.getCurrentToken();
      if (!currentToken || !currentToken.refreshToken) {
        return {
          success: false,
          error: '갱신할 토큰이 없습니다.',
        };
      }

      const result = await this.authRepository.refreshToken(currentToken.refreshToken);
      if (!result) {
        return {
          success: false,
          error: '토큰 갱신에 실패했습니다.',
        };
      }

      // 새 토큰 저장
      this.saveToken(result.token);
      AuthLocalStorageAdapter.saveSession(result.session);

      return {
        success: true,
        token: result.token,
      };
    } catch (error) {
      console.error('토큰 갱신 중 오류:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : '토큰 갱신 중 오류가 발생했습니다.',
      };
    }
  }

  /**
   * 자동 토큰 갱신 (필요 시)
   */
  async autoRefreshIfNeeded(): Promise<boolean> {
    const validation = this.validateToken();
    
    if (validation.status === TokenStatus.EXPIRED) {
      // 만료된 토큰은 갱신 시도
      const refreshResult = await this.refreshToken();
      return refreshResult.success;
    }
    
    if (validation.status === TokenStatus.REFRESH_NEEDED) {
      // 갱신이 필요한 토큰은 백그라운드에서 갱신
      this.refreshToken().catch(error => {
        console.error('백그라운드 토큰 갱신 실패:', error);
      });
      return true; // 현재 토큰은 여전히 유효
    }

    return validation.status === TokenStatus.VALID;
  }

  /**
   * JWT 페이로드 디코딩 (검증 없이)
   */
  decodeTokenPayload(token?: AuthToken): JWTPayload | null {
    const targetToken = token || this.getCurrentToken();
    if (!targetToken) {
      return null;
    }

    try {
      const payload = targetToken.accessToken.split('.')[1];
      if (!payload) {
        return null;
      }

      const decodedPayload = JSON.parse(atob(payload));
      return decodedPayload as JWTPayload;
    } catch (error) {
      console.error('JWT 페이로드 디코딩 실패:', error);
      return null;
    }
  }

  /**
   * 토큰에서 사용자 정보 추출
   */
  getUserXCircleFromToken(token?: AuthToken): {
    userId: string;
    email: string;
    role: string;
  } | null {
    const payload = this.decodeTokenPayload(token);
    if (!payload) {
      return null;
    }

    return {
      userId: payload.sub,
      email: payload.email,
      role: payload.role,
    };
  }

  /**
   * 토큰 만료까지 남은 시간 (밀리초)
   */
  getTimeUntilExpiry(token?: AuthToken): number {
    const targetToken = token || this.getCurrentToken();
    if (!targetToken) {
      return 0;
    }

    return Math.max(0, targetToken.expiresAt.getTime() - Date.now());
  }

  /**
   * 토큰 만료 시간을 사람이 읽기 쉬운 형태로 변환
   */
  getExpiryTimeString(token?: AuthToken): string {
    const timeLeft = this.getTimeUntilExpiry(token);
    
    if (timeLeft === 0) {
      return '만료됨';
    }

    const seconds = Math.floor(timeLeft / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) {
      return `${days}일 후 만료`;
    } else if (hours > 0) {
      return `${hours}시간 후 만료`;
    } else if (minutes > 0) {
      return `${minutes}분 후 만료`;
    } else {
      return `${seconds}초 후 만료`;
    }
  }

  /**
   * 토큰 자동 갱신 타이머 설정
   */
  setupAutoRefreshTimer(callback?: (success: boolean) => void): NodeJS.Timeout | null {
    const token = this.getCurrentToken();
    if (!token) {
      return null;
    }

    const timeUntilRefresh = this.getTimeUntilExpiry(token) - (5 * 60 * 1000); // 5분 전
    
    if (timeUntilRefresh <= 0) {
      // 즉시 갱신 필요
      this.refreshToken().then(result => {
        callback?.(result.success);
      });
      return null;
    }

    return setTimeout(async () => {
      const result = await this.refreshToken();
      callback?.(result.success);
      
      // 갱신 성공 시 다음 타이머 설정
      if (result.success) {
        this.setupAutoRefreshTimer(callback);
      }
    }, timeUntilRefresh);
  }

  /**
   * 모든 토큰 무효화 (로그아웃)
   */
  async invalidateAllTokens(): Promise<void> {
    try {
      const currentSession = AuthLocalStorageAdapter.loadSession();
      if (currentSession) {
        await this.authRepository.logout(
          { value: currentSession.id } as any // SessionId 타입
        );
      }
    } catch (error) {
      console.error('서버 토큰 무효화 실패:', error);
    } finally {
      // 로컬 토큰은 항상 삭제
      this.removeToken();
      AuthLocalStorageAdapter.clearAll();
    }
  }

  /**
   * 토큰 유효성 주기적 검사 설정
   */
  setupPeriodicValidation(
    intervalMs = 60000, // 1분
    onInvalid?: () => void
  ): NodeJS.Timeout {
    return setInterval(() => {
      const validation = this.validateToken();
      
      if (validation.status === TokenStatus.EXPIRED || 
          validation.status === TokenStatus.INVALID) {
        onInvalid?.();
      } else if (validation.status === TokenStatus.REFRESH_NEEDED) {
        this.refreshToken().catch(error => {
          console.error('주기적 토큰 갱신 실패:', error);
        });
      }
    }, intervalMs);
  }

  /**
   * Bearer 토큰 형태로 Authorization 헤더 값 생성
   */
  getAuthorizationHeader(token?: AuthToken): string | null {
    const targetToken = token || this.getCurrentToken();
    if (!targetToken || targetToken.isExpired()) {
      return null;
    }

    return `Bearer ${targetToken.accessToken}`;
  }

  /**
   * 토큰 상태 요약 정보
   */
  getTokenSummary(): {
    hasToken: boolean;
    isValid: boolean;
    expiresIn: number;
    userXCircle: { userId: string; email: string; role: string; } | null;
    status: TokenStatus;
  } {
    const token = this.getCurrentToken();
    const validation = this.validateToken();
    const userXCircle = this.getUserXCircleFromToken();

    return {
      hasToken: !!token,
      isValid: validation.status === TokenStatus.VALID,
      expiresIn: validation.expiresIn || 0,
      userXCircle,
      status: validation.status,
    };
  }
}